import { ArrowDownwardRounded, DeleteOutlineRounded } from "@mui/icons-material";
import { alpha, Button, Divider, Stack, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
  IssueActions,
  IssueContextProperty,
  IssueDecision,
  IssueFeed,
  IssueImpact,
  IssueProperties,
  IssueProposalsList,
  IssueTaskList,
  ItemDescriptionField,
  ShareButton,
  SignalRGroup,
} from "../../components";
import { useSession, useSignalR } from "../../hooks";
import {
  queryKeys,
  useDeleteIssue,
  useEditIssue,
  useGetIssueDetail,
  useRemoveIssueUpvote,
  useUpvoteIssue,
} from "../../http";
import { setTitlePrefix, useDestructiveConfirm } from "../../utilities";
import { DetailPageLayout } from "./DetailPageLayout";

interface UrlParams {
  issueGuid: string;
}

const IssueDetailsPage = () => {
  const user = useSession();
  const { issueGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { isLoading, data: issue } = useGetIssueDetail(issueGuid);
  const { mutate: deleteIssue } = useDeleteIssue();
  const { mutate: editIssue } = useEditIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const confirm = useDestructiveConfirm();
  const signalR = useSignalR();
  const queryClient = useQueryClient();
  const theme = useTheme();

  useEffect(() => {
    setTitlePrefix(issue?.description);
  }, [issue?.description]);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.Issue(issueGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.Issue(issueGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueGuid]);

  useEffect(() => {
    const handleShouldRefetchIssue = (issueGuid: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(issueGuid));
    };

    signalR.onShouldRefetchIssue(handleShouldRefetchIssue);
    return () => signalR.offShouldRefetchIssue(handleShouldRefetchIssue);
  }, [queryClient, signalR]);

  async function handleDescriptionChanged(value: string) {
    if (issue === undefined) {
      return;
    }

    editIssue({
      issueGuid: issue.guid,
      teamSlug: issue.assignedTeam?.slug,
      description: value,
      createdByUserId: issue.createdByUser.userId,
    });
  }

  async function handleUpvoteClicked() {
    if (issue === undefined) {
      return;
    }

    if (issue.upvotes.findIndex((x) => x.userId === user.userId) > -1) {
      removeIssueUpvote({
        issueGuid: issue.guid,
        userId: user.userId,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
      });
    } else {
      upvoteIssue({
        issueGuid: issue.guid,
        user: {
          userId: user.userId,
          displayName: user.displayName,
          enabled: true,
          profilePhotoSmallUrl: user.profilePhotoSmallUrl,
        },
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
      });
    }
  }

  async function handleDeleteClicked() {
    if (issue === undefined) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Loop",
      content: "Are you sure you want to delete this loop?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteIssue(
      {
        issueGuid: issueGuid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
      },
      {
        onSuccess: () => {
          navigate(-1);
          enqueueSnackbar(`Loop has been deleted`);
        },
      },
    );
  }

  return (
    <DetailPageLayout
      title="Loop"
      actions={
        <Stack direction="row" spacing={1}>
          <ShareButton variant="outlined" href={window.location.href} />
          <Divider orientation="vertical" />
          {issue?.permissions.userCanDeleteIssue && (
            <Button variant="outlined" color="error" onClick={handleDeleteClicked} sx={{ minWidth: 18 }}>
              <DeleteOutlineRounded />
            </Button>
          )}
        </Stack>
      }
    >
      <Stack divider={<Divider />}>
        <Stack sx={{ px: 1 }} divider={<Divider variant="dashed" />}>
          {!isLoading && (issue?.context || issue?.permissions.userCanEditContext) && (
            <IssueContextProperty issue={issue} isEditable={issue.permissions.userCanEditContext} sx={{ py: 0.25 }} />
          )}

          <ItemDescriptionField
            isLoading={isLoading}
            isEditable={issue?.permissions.userCanEditDescription ?? false}
            value={issue?.description}
            onChange={handleDescriptionChanged}
            variant="h4"
            sx={{ py: 0.5 }}
          />

          <IssueActions isLoading={isLoading} issue={issue} onUpvoteClicked={handleUpvoteClicked} sx={{ py: 0.5 }} />
        </Stack>
        <Stack
          divider={
            <ArrowDownwardRounded
              sx={{ color: alpha(theme.palette.primary.main, 0.5), fontSize: 20, alignSelf: "center" }}
            />
          }
          sx={{ p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
        >
          <IssueImpact issue={issue} isLoading={isLoading} />
          <IssueProposalsList issue={issue} isLoading={isLoading} />
          <IssueDecision issue={issue} isLoading={isLoading} />
          <IssueTaskList issueGuid={issueGuid} issue={issue} />
        </Stack>

        <IssueProperties issue={issue} isLoading={isLoading} />
        <IssueFeed issueGuid={issueGuid} />
      </Stack>
    </DetailPageLayout>
  );
};

export { IssueDetailsPage };
